import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import type { LeadPublic } from '@workspace/types';
import { contactAgentError } from '../../contact-agent/api/contactAgentScope';
import { ContactAgentPage, ContactAgentPageHeader } from '../../contact-agent/components/ContactAgentPageHeader';
import { ContactAgentTenantPicker } from '../../contact-agent/components/ContactAgentTenantPicker';
import { useContactAgentTenant } from '../../contact-agent/hooks/useContactAgentTenant';
import { selectCurrentUser } from '../../auth/authSlice';
import { leadsApi } from '../api/leadsApi';
import { formatDateTime24h } from '../utils/dates';

const STATUS_OPTIONS = ['', 'NUEVO', 'RELEVANDO', 'DERIVADO', 'CERRADO'];
const QUALIFICATION_OPTIONS = ['', 'PENDIENTE', 'CALIFICADO', 'REVISAR', 'NO_CALIFICADO', 'INSUFICIENTE'];

type VerticalOption = { key: string; label: string };
type SortOption = 'score' | 'recent';

function scoreTone(score: number | null): string {
  if (score === null) {
    return 'text-muted-foreground';
  }
  if (score >= 70) {
    return 'text-emerald-600';
  }
  if (score >= 40) {
    return 'text-amber-600';
  }
  return 'text-rose-600';
}

function qualificationVariant(qualification: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (qualification === 'CALIFICADO') {
    return 'default';
  }
  if (qualification === 'REVISAR' || qualification === 'PENDIENTE') {
    return 'secondary';
  }
  if (qualification === 'NO_CALIFICADO') {
    return 'destructive';
  }
  return 'outline';
}

export function LeadsBoardPage() {
  const { tenantId, tenants, isSuperAdmin, ready, selectTenant } = useContactAgentTenant();
  const currentUser = useSelector(selectCurrentUser);
  // El vendedor (OPERATOR) no tiene el dashboard del embudo: vuelve al Inbox.
  const backTo = currentUser?.role === 'OPERATOR' ? '/admin/inbox' : '/admin/leads';
  const [leads, setLeads] = useState<LeadPublic[]>([]);
  const [verticalOptions, setVerticalOptions] = useState<VerticalOption[]>([]);
  const [selectedVerticals, setSelectedVerticals] = useState<string[]>([]);
  const [status, setStatus] = useState('');
  const [qualification, setQualification] = useState('');
  const [sort, setSort] = useState<SortOption>('score');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!ready) {
      return;
    }
    let cancelled = false;
    void leadsApi
      .listVerticals()
      .then(options => {
        if (!cancelled) {
          setVerticalOptions(options);
        }
      })
      .catch(() => {
        // Sin config activa no hay filtro por vertical; el tablero sigue funcionando.
      });
    return () => {
      cancelled = true;
    };
  }, [ready, tenantId]);

  useEffect(() => {
    if (!ready) {
      return;
    }
    let cancelled = false;
    setError('');
    void leadsApi
      .listLeads({
        ...(status ? { status } : {}),
        ...(qualification ? { qualification } : {}),
        ...(selectedVerticals.length ? { verticals: selectedVerticals } : {}),
        sort,
      })
      .then(items => {
        if (!cancelled) {
          setLeads(items);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(contactAgentError(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ready, tenantId, status, qualification, selectedVerticals, sort]);

  const toggleVertical = (key: string) => {
    setSelectedVerticals(current =>
      current.includes(key) ? current.filter(item => item !== key) : [...current, key],
    );
  };

  return (
    <ContactAgentPage>
      <ContactAgentPageHeader
        title='Tablero de leads'
        description='Oportunidades entrantes con estado, calificación y score.'
        backTo={backTo}
        toolbar={
          isSuperAdmin ? (
            <ContactAgentTenantPicker tenants={tenants} tenantId={tenantId} onChange={selectTenant} />
          ) : null
        }
      />

      <div className='mb-3 flex flex-wrap items-center gap-3'>
        <label className='text-sm'>
          Estado{' '}
          <select
            className='ml-2 rounded-md border border-input bg-background px-2 py-1'
            value={status}
            onChange={event => setStatus(event.target.value)}
          >
            {STATUS_OPTIONS.map(option => (
              <option key={option || 'all'} value={option}>
                {option || 'Todos'}
              </option>
            ))}
          </select>
        </label>
        <label className='text-sm'>
          Calificación{' '}
          <select
            className='ml-2 rounded-md border border-input bg-background px-2 py-1'
            value={qualification}
            onChange={event => setQualification(event.target.value)}
          >
            {QUALIFICATION_OPTIONS.map(option => (
              <option key={option || 'all'} value={option}>
                {option || 'Todas'}
              </option>
            ))}
          </select>
        </label>
        <label className='text-sm'>
          Ordenar por{' '}
          <select
            className='ml-2 rounded-md border border-input bg-background px-2 py-1'
            value={sort}
            onChange={event => setSort(event.target.value === 'recent' ? 'recent' : 'score')}
          >
            <option value='score'>Mayor score</option>
            <option value='recent'>Más recientes</option>
          </select>
        </label>
      </div>

      {verticalOptions.length > 0 ? (
        <div className='mb-4 flex flex-wrap items-center gap-2'>
          <span className='text-sm text-muted-foreground'>Vertical:</span>
          <Button
            type='button'
            size='sm'
            variant={selectedVerticals.length === 0 ? 'default' : 'outline'}
            onClick={() => setSelectedVerticals([])}
          >
            Todas
          </Button>
          {verticalOptions.map(option => (
            <Button
              key={option.key}
              type='button'
              size='sm'
              variant={selectedVerticals.includes(option.key) ? 'default' : 'outline'}
              onClick={() => toggleVertical(option.key)}
            >
              {option.label}
            </Button>
          ))}
        </div>
      ) : null}

      {error ? <p className='text-sm text-destructive'>{error}</p> : null}

      <div className='space-y-3'>
        {leads.map(lead => (
          <Card key={lead.id} className='rounded-2xl'>
            <CardContent className='flex flex-wrap items-center gap-4 py-4'>
              <div className='flex min-w-[64px] flex-col items-center'>
                <span className={`text-3xl font-bold ${scoreTone(lead.score)}`}>
                  {lead.score ?? '—'}
                </span>
                <span className='text-[10px] uppercase tracking-wide text-muted-foreground'>score</span>
              </div>
              <div className='min-w-0 flex-1'>
                <p className='truncate text-base font-semibold'>
                  {lead.companyName || lead.contactName || 'Lead sin nombre'}
                </p>
                <p className='truncate text-sm text-muted-foreground'>
                  {[lead.contactName, lead.verticalLabel || lead.verticalResolved || lead.verticalDeclared]
                    .filter(Boolean)
                    .join(' · ') || 'Vertical pendiente'}
                </p>
                <p className='text-xs text-muted-foreground'>
                  Canal: {lead.sourceChannel ?? '—'} · Creado:{' '}
                  {formatDateTime24h(lead.createdAt)}
                </p>
              </div>
              <div className='flex flex-col items-end gap-2'>
                <div className='flex gap-2'>
                  <Badge variant='outline'>{lead.status}</Badge>
                  <Badge variant={qualificationVariant(lead.qualification)}>{lead.qualification}</Badge>
                </div>
                <Button variant='outline' size='sm' asChild>
                  <Link to={`/admin/leads/${lead.id}`}>Ver detalle</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {ready && leads.length === 0 && !error ? (
        <p className='text-sm text-muted-foreground'>No hay leads con los filtros seleccionados.</p>
      ) : null}
    </ContactAgentPage>
  );
}
